import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Calendar, User, BookOpen } from 'lucide-react';
import { FadeIn } from '@/components/Animations';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const defaultPosts = [
  {
    id: '1',
    title: 'The Future of Gig Economy in South Africa',
    slug: 'future-gig-economy-south-africa',
    excerpt: 'As the gig economy continues to grow, what does the future hold for delivery riders and independent contractors?',
    author: 'Andrew Clarke',
    category: 'Industry Insights',
    image_url: 'https://images.unsplash.com/photo-1588620862555-4da1a7c523b8?auto=format&fit=crop&q=80&w=600',
    created_at: '2024-01-15',
  },
  {
    id: '2',
    title: 'Road Safety Tips Every Delivery Rider Should Know',
    slug: 'road-safety-tips-delivery-riders',
    excerpt: 'Essential safety practices that can help protect riders on South African roads.',
    author: 'Andrew Clarke',
    category: 'Safety',
    image_url: 'https://images.unsplash.com/photo-1577199001468-44c049e7603f?auto=format&fit=crop&q=80&w=600',
    created_at: '2024-01-10',
  },
  {
    id: '3',
    title: 'How Corporate CSI Can Transform Lives',
    slug: 'corporate-csi-transform-lives',
    excerpt: 'Discover how corporate social investment is making a real difference in the lives of delivery riders.',
    author: 'Andrew Clarke',
    category: 'Partnerships',
    image_url: 'https://images.unsplash.com/photo-1758519288905-38b7b00c1023?auto=format&fit=crop&q=80&w=600',
    created_at: '2024-01-05',
  },
];

const InsightsPage = () => {
  const [posts, setPosts] = useState(defaultPosts);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await fetch(`${API_URL}/api/blog`);
        if (response.ok) {
          const data = await response.json();
          if (data.length > 0) {
            setPosts(data);
          }
        }
      } catch (error) {
        console.error('Failed to fetch posts:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-ZA', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div className="min-h-screen pt-20" data-testid="insights-page">
      {/* Hero */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-slate-50 via-purple-50/30 to-white">
        <div className="container-wide">
          <FadeIn>
            <div className="max-w-4xl">
              <span className="inline-block px-4 py-2 bg-purple-100 text-primary-mlm rounded-full text-sm font-semibold tracking-wide uppercase mb-6">
                Insights & News
              </span>
              <h1 className="text-4xl md:text-6xl font-heading font-bold text-slate-900 mb-6">
                Latest from <span className="text-primary-mlm">#MyLifeMatters</span>
              </h1>
              <p className="text-xl text-slate-600 leading-relaxed">
                Stay updated with the latest news, insights, and stories from the gig economy 
                reform movement.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="py-20 md:py-32 bg-white">
        <div className="container-wide">
          {posts.length === 0 ? (
            <FadeIn>
              <div className="text-center py-16">
                <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-6" />
                <h2 className="text-2xl font-heading font-semibold text-slate-900 mb-4">
                  Coming Soon
                </h2>
                <p className="text-slate-600 max-w-md mx-auto">
                  We're working on bringing you valuable insights about the gig economy, 
                  rider safety, and our movement. Check back soon!
                </p>
              </div>
            </FadeIn>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post, index) => (
                <FadeIn key={post.id} delay={0.1 * index}>
                  <article className="bg-white rounded-2xl border border-slate-100 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300 h-full flex flex-col">
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={post.image_url || 'https://images.unsplash.com/photo-1588620862555-4da1a7c523b8?auto=format&fit=crop&q=80&w=600'}
                        alt={post.title}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-6 flex-1 flex flex-col">
                      <span className="inline-block px-3 py-1 bg-purple-100 text-primary-mlm text-xs font-semibold rounded-full mb-4 self-start">
                        {post.category}
                      </span>
                      <h3 className="text-xl font-heading font-semibold text-slate-900 mb-3 line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="text-slate-600 text-sm mb-4 flex-1 line-clamp-3">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                        <div className="flex items-center gap-4 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {post.author}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(post.created_at)}
                          </span>
                        </div>
                      </div>
                      <Link
                        to={`/insights/${post.slug}`}
                        className="mt-4 text-primary-mlm hover:text-[#5b21b6] font-semibold text-sm flex items-center gap-1"
                        data-testid={`read-post-${post.slug}`}
                      >
                        Read More
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </article>
                </FadeIn>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-32 bg-slate-50">
        <div className="container-wide">
          <FadeIn>
            <div className="bg-gradient-to-br from-primary-mlm to-purple-700 rounded-3xl p-8 md:p-16 text-center">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-6">
                Want to Share Your Story?
              </h2>
              <p className="text-xl text-purple-100 max-w-2xl mx-auto mb-8">
                Are you a rider with a story to tell? A partner with insights to share? 
                We'd love to hear from you.
              </p>
              <Button
                asChild
                className="bg-white text-primary-mlm hover:bg-slate-100 rounded-full px-8 py-6 font-semibold"
                data-testid="insights-contact-btn"
              >
                <Link to="/contact">
                  Get In Touch
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

export default InsightsPage;
